import { useEffect, useRef } from 'react';

/**
 * Make a layer that sits on top of a view (a full-screen overlay, a photo viewer)
 * respond to the phone's back gesture by closing itself, instead of the gesture
 * skipping it and navigating the whole app back.
 *
 * While `isOpen`, the hook pushes one history entry that copies the current
 * state and tags it with `_modal: token`. App.tsx ignores pops that land on a
 * `_modal` entry for the current view, so a back gesture pops the marker,
 * this hook calls `onClose`, and the app's own view stays put.
 *
 * Layers nest: each open layer pushes its own marker, so back closes the top
 * layer only. A pop that lands on this layer's own marker (a layer above it
 * just closed) is ignored.
 *
 * Closing from the screen (a Back button) runs the cleanup, which removes the
 * marker with `history.back()` when it is still on top — so the next back
 * gesture never needs a second swipe.
 */
export function useBackToClose(isOpen: boolean, onClose: () => void, token: string): void {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return;
    const base = (window.history.state && typeof window.history.state === 'object') ? window.history.state : {};
    window.history.pushState({ ...base, _modal: token }, '');

    let closedByPop = false;
    const handlePop = (event: PopStateEvent) => {
      const state = event.state as { _modal?: string } | null;
      if (state?._modal === token) return;
      closedByPop = true;
      onCloseRef.current();
    };
    window.addEventListener('popstate', handlePop);

    return () => {
      window.removeEventListener('popstate', handlePop);
      const top = window.history.state as { _modal?: string } | null;
      if (!closedByPop && top?._modal === token) window.history.back();
    };
  }, [isOpen, token]);
}
